import {
  bulkMarkingInstruction,
  DotMarkingContainer,
  dotMarkingGroupFor,
  DotMarkingGroup,
  ID_MARKING_PAGE_INSTRUCTION,
  inhalationMarkingHeightMm,
  inhalationMarkingInstruction,
  ORIENTATION_ARROWS_INSTRUCTION,
  packageLabelInstruction,
  printsIdMarkingStrip,
  requiresHazardLabel,
  requiresLargeIdNumber,
  requiresNonBulkMarkings,
} from "./dotMarkingGroup";
import {
  designPrintsDiamond,
  diamondForHazardClass,
  printsRegulatedHazardLabel,
} from "./hazardClassDiamond";
import { DotBlockContent, hasDotBlock, poisonInhalationLine } from "./hazardousWasteLabelVisuals";

/** The label fields the DOT block's marking decisions read. */
export interface DotBlockMarkingContent {
  dotHazardClass?: string | null;
  dotSubsidiaryHazardClass?: string | null;
  dotIdNumber?: string | null;
  physicalState?: string | null;
}

export interface DotBlockMarking {
  showHazardLabels: boolean;
  /** How many full-size regulated label pages print (0-2). Class 7 draws a
   * diamond but takes no regulated label, so it does not count. */
  regulatedLabelPages: number;
  showOrientationArrows: boolean;
  inhalationLine: string | undefined;
  markingInstruction: string | undefined;
}

/** 49 CFR 172.312 -- LIQUID only. SLUDGE, GAS and POWDER are not "liquid" for
 * 172.312, and the rule is for non-bulk packagings. The enum arrives
 * PascalCase from GraphQL and SCREAMING_CASE from Prisma. */
function takesOrientationArrows(
  group: DotMarkingGroup | undefined,
  physicalState: string | null | undefined
): boolean {
  return (
    group !== undefined &&
    requiresNonBulkMarkings(group) &&
    physicalState?.trim().toUpperCase() === "LIQUID"
  );
}

/** The label page placement, or the ID number page's when no label prints. */
function packagePageInstruction(regulated: number, printsStrip: boolean): string | undefined {
  if (regulated > 0) {
    return packageLabelInstruction(regulated === 2);
  }
  return printsStrip ? ID_MARKING_PAGE_INSTRUCTION : undefined;
}

/** The package instruction: the page placement and the arrows. */
function packageInstruction(
  regulated: number,
  printsStrip: boolean,
  hasArrows: boolean
): string | undefined {
  const parts = [
    packagePageInstruction(regulated, printsStrip),
    hasArrows ? ORIENTATION_ARROWS_INSTRUCTION : undefined,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : undefined;
}

/**
 * The marking decisions the PDF and Oak's preview share. `showDotBlock` is
 * false when the stream has no DOT profile, which suppresses the hazard
 * labels. An undefined `container` is an unknown container type: no hazard
 * labels, no inhalation line, no instruction.
 *
 * The instruction is the package hazard-label placement (49 CFR 172.406) and
 * orientation arrows (172.312), or the bulk/portable-tank placement. The two
 * are mutually exclusive, so at most one applies.
 */
export function resolveDotBlockMarking(
  content: DotBlockMarkingContent,
  container: DotMarkingContainer | undefined,
  showDotBlock = true
): DotBlockMarking {
  const markingGroup = container ? dotMarkingGroupFor(container) : undefined;
  const showHazardLabels =
    showDotBlock &&
    markingGroup !== undefined &&
    container !== undefined &&
    requiresHazardLabel(markingGroup, container);
  // 49 CFR 172.313(a) applies to bulk packagings too, so this does not depend
  // on showHazardLabels.
  const inhalationLine =
    markingGroup === undefined ? undefined : poisonInhalationLine(content.dotHazardClass);
  const showOrientationArrows =
    showHazardLabels && takesOrientationArrows(markingGroup, content.physicalState);

  // Counted from the labels that print at full size: class 7 draws the
  // trefoil on the label page but takes no regulated label.
  const regulated = showHazardLabels
    ? [content.dotHazardClass, content.dotSubsidiaryHazardClass].filter(printsRegulatedHazardLabel)
        .length
    : 0;
  const base = {
    showHazardLabels,
    regulatedLabelPages: regulated,
    showOrientationArrows,
    inhalationLine,
  };
  if (markingGroup === undefined) {
    return { ...base, markingInstruction: undefined };
  }
  if (!requiresLargeIdNumber(markingGroup)) {
    return {
      ...base,
      markingInstruction: packageInstruction(
        regulated,
        showDotBlock &&
          container !== undefined &&
          printsIdMarkingStrip(container, content.dotIdNumber),
        showOrientationArrows
      ),
    };
  }
  const hasIdNumber = Boolean(content.dotIdNumber);
  if (hasIdNumber || regulated > 0) {
    return {
      ...base,
      markingInstruction: bulkMarkingInstruction(markingGroup, container, {
        hasIdNumber,
        printsLabels: regulated > 0,
        printsBothLabels: regulated === 2,
        // The 2.3 label carries the words, and 172.313(a) then needs no marking.
        hasInhalationMarking: inhalationLine !== undefined && regulated === 0,
      }),
    };
  }
  // No ID-number page and no label, but the inhalation marking still goes on two sides.
  return {
    ...base,
    markingInstruction: inhalationLine
      ? inhalationMarkingInstruction(inhalationMarkingHeightMm(markingGroup, container))
      : undefined,
  };
}

/**
 * Whether the label draws at least one DOT hazard diamond: the stream has a DOT
 * profile, the packaging takes the hazard label, and a class has a design with a
 * symbol. A class with no division ("2", "6") has no symbol and draws nothing.
 */
export function dotDiamondPrints(
  content: DotBlockMarkingContent & DotBlockContent,
  container: DotMarkingContainer | undefined
): boolean {
  const { showHazardLabels } = resolveDotBlockMarking(content, container, hasDotBlock(content));
  return (
    showHazardLabels &&
    [content.dotHazardClass, content.dotSubsidiaryHazardClass].some((hazardClass) => {
      const design = hazardClass ? diamondForHazardClass(hazardClass)?.design : undefined;
      return design !== undefined && designPrintsDiamond(design);
    })
  );
}
